// The stereo stage: every sound of the song as a dot on a stage seen from the listener (bottom).
// Left/right is the pan; up is further back (more room: the reverb send), down is in front (dry).
// Drag a dot, or focus it and use the arrow keys. "Studio mix" sets up the stage like a sound
// engineer would (kick, snare and bass centred and dry in front, hats and percussion spread, chords
// and the classic instrument either side of the lead) as a starting point; "Mirror" flips it.
import { useRef } from "react";
import {
  defaultMix,
  laneSlot,
  loopSound,
  mixGroupOf,
  MIX_RANGE,
  NUM_LANE_SOUNDS,
  NUM_SOUNDS,
  studioMix,
  type SoundMix,
  isAtmosPad,
} from "@eb/shared";
import { setMix } from "./ops";
import { beginGesture, edit, editSong, endGesture, useEditor } from "./store";

const W = 320;
const H = 170;
const PAD = 22;
const ROOM_MAX = MIX_RANGE.reverb_send[1];

const xOf = (pan: number) => W / 2 + pan * (W / 2 - PAD);
const yOf = (room: number) => H - 34 - (room / ROOM_MAX) * (H - 34 - PAD);
const panOf = (x: number) => Math.max(-1, Math.min(1, (x - W / 2) / (W / 2 - PAD)));
const roomOf = (y: number) => Math.max(0, Math.min(ROOM_MAX, ((H - 34 - y) / (H - 34 - PAD)) * ROOM_MAX));
const panText = (v: number) =>
  Math.abs(v) < 0.02 ? "centre" : `${Math.round(Math.abs(v) * 100)}% ${v < 0 ? "left" : "right"}`;

const INSTRUMENT_LABEL = ["Bass", "Chords", "Lead", "Classic"];

function groupClass(slot: number, name: string): string {
  if (isAtmosPad(slot)) return "stage-dot inst-atmos";
  if (slot >= 16) return `stage-dot inst-${INSTRUMENT_LABEL[laneSlot(slot) - 16].toLowerCase()}`;
  return `stage-dot drum-${mixGroupOf(name)}`;
}

export function StagePanel() {
  const spec = useEditor((s) => s.spec);
  const song = useEditor((s) => s.hist.present);
  const selected = useEditor((s) => s.selectedSlot);
  const svg = useRef<SVGSVGElement>(null);
  const dragging = useRef<number | null>(null);
  if (!spec) return null;
  const defaults = defaultMix(spec);
  const mixOf = (i: number): SoundMix => song.mix[i] ?? defaults[i];
  // 20 sounds, or 28 with extra lanes.
  const full = () => Array.from({ length: song.lanes?.length ? NUM_LANE_SOUNDS : NUM_SOUNDS }, (_, i) => mixOf(i));
  // The sounds the song plays (any pattern), plus the selected one.
  const used = new Set<number>([selected, ...(song.lanes ?? [])]);
  for (const p of [song.events, ...(song.extra_patterns ?? [])]) for (const e of p) used.add(e.pad);
  const slots = [...used].filter((i) => i < NUM_LANE_SOUNDS).sort((a, b) => a - b);
  const nameOf = (i: number) =>
    i < 16 || isAtmosPad(i)
      ? spec.sounds[i].name
      : `${INSTRUMENT_LABEL[laneSlot(i) - 16]}${i >= NUM_SOUNDS ? " +" : ""}: ${loopSound(spec, song.variants, i)?.name ?? ""}`;
  const shortOf = (i: number) =>
    i < 16 || isAtmosPad(i)
      ? spec.sounds[i].name.replace(/^(Snare|Kick|Hat) /, "")
      : `${INSTRUMENT_LABEL[laneSlot(i) - 16]}${i >= NUM_SOUNDS ? " +" : ""}`;

  const toLocal = (e: React.PointerEvent) => {
    const m = svg.current?.getScreenCTM();
    if (!m) return null;
    const p = new DOMPoint(e.clientX, e.clientY).matrixTransform(m.inverse());
    return { x: p.x, y: p.y };
  };
  const place = (slot: number, patch: Partial<SoundMix>) => edit((l) => setMix(l, slot, patch, defaults));
  const nudge = (slot: number, patch: Partial<SoundMix>) => {
    beginGesture("Stage");
    place(slot, patch);
    endGesture();
  };

  return (
    <section className="stage-panel panel" aria-label="Stereo stage">
      <div className="row stage-head">
        <strong>Stereo stage</strong>
        <span className="spacer" />
        <button
          type="button"
          className="btn btn-small"
          title="Set up the stage like a studio: kick, snare and bass centred and dry in front, hats and percussion spread, chords and classic either side of the lead"
          onClick={() => {
            editSong((l) => ({ ...l, mix: studioMix(spec, full()) }));
          }}
          data-testid="studio-mix"
        >
          Studio mix
        </button>
        <button
          type="button"
          className="btn btn-small btn-ghost"
          title="Flip the stage left ↔ right"
          onClick={() => {
            editSong((l) => ({ ...l, mix: full().map((m) => ({ ...m, pan: -m.pan })) }));
          }}
          data-testid="stage-mirror"
        >
          Mirror
        </button>
      </div>
      <svg
        ref={svg}
        className="stage"
        viewBox={`0 0 ${W} ${H}`}
        role="group"
        aria-label="Sounds on the stereo stage: left to right is pan, up is further back"
        onPointerMove={(e) => {
          const slot = dragging.current;
          if (slot === null) return;
          const p = toLocal(e);
          if (p)
            place(slot, { pan: Math.round(panOf(p.x) * 100) / 100, reverb_send: Math.round(roomOf(p.y) * 100) / 100 });
        }}
        onPointerUp={() => {
          if (dragging.current !== null) endGesture();
          dragging.current = null;
        }}
        onPointerCancel={() => {
          if (dragging.current !== null) endGesture();
          dragging.current = null;
        }}
      >
        <rect x={1} y={1} width={W - 2} height={H - 2} rx={10} className="stage-floor" />
        <line x1={W / 2} y1={PAD - 6} x2={W / 2} y2={H - 30} className="stage-centre" />
        <text x={8} y={14} className="stage-hint">
          back (room)
        </text>
        <text x={8} y={H - 22} className="stage-hint">
          front (dry)
        </text>
        <text x={PAD - 8} y={H - 8} className="stage-hint">
          L
        </text>
        <text x={W - PAD + 2} y={H - 8} className="stage-hint">
          R
        </text>
        <path d={`M${W / 2 - 9} ${H - 4} l9 -10 l9 10 z`} className="stage-listener" aria-hidden="true" />
        {slots.map((i) => {
          const m = mixOf(i);
          const x = xOf(m.pan);
          const y = yOf(m.reverb_send);
          return (
            <g
              key={i}
              className={`${groupClass(i, spec.sounds[laneSlot(i)].name)}${i === selected ? " selected" : ""}`}
              transform={`translate(${x.toFixed(1)} ${y.toFixed(1)})`}
              tabIndex={0}
              role="slider"
              aria-label={`${nameOf(i)}: ${panText(m.pan)}, room ${Math.round((m.reverb_send / ROOM_MAX) * 100)}%`}
              aria-valuemin={-100}
              aria-valuemax={100}
              aria-valuenow={Math.round(m.pan * 100)}
              aria-valuetext={`${panText(m.pan)}, room ${Math.round((m.reverb_send / ROOM_MAX) * 100)}%`}
              data-testid={`stage-${i}`}
              onPointerDown={(e) => {
                e.preventDefault();
                (e.currentTarget.ownerSVGElement ?? e.currentTarget).setPointerCapture?.(e.pointerId);
                useEditor.getState().set({ selectedSlot: i });
                dragging.current = i;
                beginGesture("Stage");
              }}
              onFocus={() => useEditor.getState().set({ selectedSlot: i })}
              onKeyDown={(e) => {
                const step = e.shiftKey ? 0.01 : 0.05;
                if (e.key === "ArrowLeft") nudge(i, { pan: Math.max(-1, Math.round((m.pan - step) * 100) / 100) });
                else if (e.key === "ArrowRight") nudge(i, { pan: Math.min(1, Math.round((m.pan + step) * 100) / 100) });
                else if (e.key === "ArrowUp")
                  nudge(i, { reverb_send: Math.min(ROOM_MAX, Math.round((m.reverb_send + step / 2) * 100) / 100) });
                else if (e.key === "ArrowDown")
                  nudge(i, { reverb_send: Math.max(0, Math.round((m.reverb_send - step / 2) * 100) / 100) });
                else return;
                e.preventDefault();
              }}
            >
              <circle r={8 + 4 * m.volume} />
              <text y={-13} textAnchor="middle">
                {shortOf(i)}
              </text>
            </g>
          );
        })}
      </svg>
      <p className="muted small">
        Drag a sound: left/right pans it, up puts it further back (more room). Arrow keys work too.
      </p>
    </section>
  );
}
